import { useState, useEffect } from 'react';
import { Card, Form, Input, Button, message, Switch, Spin, Alert, InputNumber } from 'antd';
import { CreditCardOutlined, DollarOutlined, SaveOutlined, RocketOutlined, GlobalOutlined } from '@ant-design/icons';
import { adminAPI } from '../services/api';

const PaymentSettings = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const [stripeEnabled, setStripeEnabled] = useState(true);
  const [paypalEnabled, setPaypalEnabled] = useState(true);
  const [paypalSandbox, setPaypalSandbox] = useState(true);
  const [fastspringEnabled, setFastSpringEnabled] = useState(false);
  const [fastspringEnv, setFastSpringEnv] = useState('test');
  const [fastspringTimeout, setFastSpringTimeout] = useState(300);
  const [airwallexEnabled, setAirwallexEnabled] = useState(false);
  const [airwallexEnv, setAirwallexEnv] = useState('demo');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getConfigs();
      
      let configsArray = [];
      if (response.code === 0) {
        if (Array.isArray(response.data)) {
          configsArray = response.data;
        } else if (response.data && response.data.list && Array.isArray(response.data.list)) {
          configsArray = response.data.list;
        } else if (response.data && typeof response.data === 'object') {
          configsArray = Object.entries(response.data).map(([key, value]) => ({
            config_key: key,
            config_value: value
          }));
        }
      }
      
      if (configsArray.length > 0) {
        const cfgMap = {};
        configsArray.forEach((item) => {
          cfgMap[item.config_key] = item.config_value;
        });
        
        form.setFieldsValue({
          stripe_secret_key: cfgMap.stripe_secret_key || '',
          stripe_webhook_secret: cfgMap.stripe_webhook_secret || '',
          paypal_client_id: cfgMap.paypal_client_id || '',
          paypal_client_secret: cfgMap.paypal_client_secret || '',
          fastspring_api_key: cfgMap.fastspring_api_key || '',
          fastspring_store_id: cfgMap.fastspring_store_id || '',
          fastspring_webhook_url: cfgMap.fastspring_webhook_url || '',
          fastspring_webhook_secret: cfgMap.fastspring_webhook_secret || '',
          fastspring_product: cfgMap.fastspring_product || 'blueapi-ai-product',
          airwallex_client_id: cfgMap.airwallex_client_id || '',
          airwallex_api_key: cfgMap.airwallex_api_key || '',
          airwallex_webhook_secret: cfgMap.airwallex_webhook_secret || '',
          airwallex_api_base_url: cfgMap.airwallex_api_base_url || 'https://api-demo.airwallex.com',
        });
        
        setStripeEnabled(cfgMap.stripe_enabled !== 'false');
        setPaypalEnabled(cfgMap.paypal_enabled !== 'false');
        setPaypalSandbox(cfgMap.paypal_sandbox === 'true');
        setFastSpringEnabled(cfgMap.fastspring_enabled === 'true');
        setFastSpringEnv(cfgMap.fastspring_environment || 'test');
        setFastSpringTimeout(parseInt(cfgMap.fastspring_order_timeout) || 300);
        setAirwallexEnabled(cfgMap.airwallex_enabled === 'true');
        setAirwallexEnv(cfgMap.airwallex_environment || 'demo');
      }
    } catch (error) {
      console.error('获取配置失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const values = await form.validateFields();
      
      const configsList = [
        { key: 'stripe_secret_key', value: values.stripe_secret_key || '' },
        { key: 'stripe_webhook_secret', value: values.stripe_webhook_secret || '' },
        { key: 'stripe_enabled', value: stripeEnabled ? 'true' : 'false' },
        { key: 'paypal_client_id', value: values.paypal_client_id || '' },
        { key: 'paypal_client_secret', value: values.paypal_client_secret || '' },
        { key: 'paypal_enabled', value: paypalEnabled ? 'true' : 'false' },
        { key: 'paypal_sandbox', value: paypalSandbox ? 'true' : 'false' },
        { key: 'fastspring_api_key', value: values.fastspring_api_key || '' },
        { key: 'fastspring_store_id', value: values.fastspring_store_id || '' },
        { key: 'fastspring_webhook_url', value: values.fastspring_webhook_url || '' },
        { key: 'fastspring_webhook_secret', value: values.fastspring_webhook_secret || '' },
        { key: 'fastspring_enabled', value: fastspringEnabled ? 'true' : 'false' },
        { key: 'fastspring_environment', value: fastspringEnv },
        { key: 'fastspring_order_timeout', value: String(fastspringTimeout) },
        { key: 'fastspring_product', value: values.fastspring_product || 'blueapi-ai-product' },
        { key: 'airwallex_enabled', value: airwallexEnabled ? 'true' : 'false' },
        { key: 'airwallex_environment', value: airwallexEnv },
        { key: 'airwallex_client_id', value: values.airwallex_client_id || '' },
        { key: 'airwallex_api_key', value: values.airwallex_api_key || '' },
        { key: 'airwallex_webhook_secret', value: values.airwallex_webhook_secret || '' },
        { key: 'airwallex_api_base_url', value: values.airwallex_api_base_url || 'https://api-demo.airwallex.com' },
      ];

      for (const cfg of configsList) {
        await adminAPI.updateConfigByKey(cfg.key, cfg.value);
      }

      message.success('支付配置保存成功');
    } catch (error) {
      console.error('保存配置失败:', error);
      message.error('保存配置失败');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-page-wrapper">
      <div className="admin-page-container">
        <div className="page-header">
          <h2>支付账户配置</h2>
          <p>配置 Stripe、PayPal、FastSpring 和 Airwallex 支付账户信息</p>
        </div>

        <Spin spinning={loading}>
          <Form form={form} layout="vertical">
            {/* Stripe 配置 */}
            <Card 
              title={
                <span>
                  <CreditCardOutlined style={{ marginRight: 8 }} />
                  Stripe 支付
                </span>
              }
              style={{ marginBottom: 24 }}
              extra={
                <Switch
                  checkedChildren="启用"
                  unCheckedChildren="禁用"
                  checked={stripeEnabled}
                  onChange={setStripeEnabled}
                />
              }
            >
              <Alert
                message="获取 Stripe API 密钥"
                description={
                  <span>
                    请前往 <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer">Stripe Dashboard</a> 获取 Secret Key 和 Webhook Secret。
                    Webhook 端点地址为：<code>/api/webhook/stripe</code>
                  </span>
                }
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
              <Form.Item
                name="stripe_secret_key"
                label="Stripe Secret Key"
                rules={[{ required: stripeEnabled, message: '请输入 Stripe Secret Key' }]}
              >
                <Input.Password placeholder="sk_live_xxx 或 sk_test_xxx" />
              </Form.Item>
              <Form.Item
                name="stripe_webhook_secret"
                label="Stripe Webhook Secret"
                rules={[{ required: stripeEnabled, message: '请输入 Webhook Secret' }]}
              >
                <Input.Password placeholder="whsec_xxx" />
              </Form.Item>
            </Card>

            {/* PayPal 配置 */}
            <Card
              title={
                <span>
                  <DollarOutlined style={{ marginRight: 8 }} />
                  PayPal 支付
                </span>
              }
              style={{ marginBottom: 24 }}
              extra={
                <Switch
                  checkedChildren="启用"
                  unCheckedChildren="禁用"
                  checked={paypalEnabled}
                  onChange={setPaypalEnabled}
                />
              }
            >
              <Alert
                message="获取 PayPal API 凭证"
                description={
                  <span>
                    请前往 <a href="https://developer.paypal.com/dashboard/" target="_blank" rel="noopener noreferrer">PayPal Developer Dashboard</a> 创建应用并获取 Client ID 和 Client Secret。
                  </span>
                }
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
              <Form.Item
                name="paypal_client_id"
                label="PayPal Client ID"
                rules={[{ required: paypalEnabled, message: '请输入 PayPal Client ID' }]}
              >
                <Input placeholder="PayPal Client ID" />
              </Form.Item>
              <Form.Item
                name="paypal_client_secret"
                label="PayPal Client Secret"
                rules={[{ required: paypalEnabled, message: '请输入 PayPal Client Secret' }]}
              >
                <Input.Password placeholder="PayPal Client Secret" />
              </Form.Item>
              <Form.Item label="沙盒模式">
                <Switch
                  checkedChildren="沙盒"
                  unCheckedChildren="生产"
                  checked={paypalSandbox}
                  onChange={setPaypalSandbox}
                />
                <span style={{ marginLeft: 8, color: '#6b7280' }}>
                  {paypalSandbox ? '当前使用 PayPal 沙盒环境测试' : '当前使用 PayPal 生产环境'}
                </span>
              </Form.Item>
            </Card>

            {/* FastSpring 配置 */}
            <Card
              title={
                <span>
                  <RocketOutlined style={{ marginRight: 8 }} />
                  FastSpring 支付
                </span>
              }
              style={{ marginBottom: 24 }}
              extra={
                <Switch
                  checkedChildren="启用"
                  unCheckedChildren="禁用"
                  checked={fastspringEnabled}
                  onChange={setFastSpringEnabled}
                />
              }
            >
              <Alert
                message="获取 FastSpring API 凭证"
                description={
                  <span>
                    请前往 <a href="https://www.fastspring.com/dashboard/" target="_blank" rel="noopener noreferrer">FastSpring Dashboard</a> 获取 API Key 和配置 Webhook。
                    Webhook 端点地址为：<code>/api/webhook/fastspring</code>
                  </span>
                }
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
              
              <Form.Item
                name="fastspring_api_key"
                label="FastSpring API Key"
                rules={[{ required: fastspringEnabled, message: '请输入 FastSpring API Key' }]}
              >
                <Input.Password placeholder="api_user:api_secret" />
              </Form.Item>
              
              <Form.Item
                name="fastspring_store_id"
                label="FastSpring Store ID"
                rules={[{ required: fastspringEnabled, message: '请输入 Store ID' }]}
              >
                <Input placeholder={fastspringEnv === 'test' ? 'your-store.test' : 'your-store'} />
              </Form.Item>
              
              <Form.Item
                name="fastspring_webhook_url"
                label="FastSpring Webhook URL"
                extra="填写支付回调地址，如：https://your-domain.com/api/webhook/fastspring"
              >
                <Input placeholder="https://your-domain.com/api/webhook/fastspring" />
              </Form.Item>

              <Form.Item
                name="fastspring_webhook_secret"
                label="FastSpring Webhook Secret"
                extra="用于验证 Webhook 签名，防止伪造回调请求。在 FastSpring Dashboard 的 Webhook 设置中获取。"
              >
                <Input.Password placeholder="HMAC SHA256 签名密钥" />
              </Form.Item>

              <Form.Item
                name="fastspring_product"
                label="FastSpring 产品 ID"
                extra={
                  <span style={{ color: '#999' }}>
                    所有金额（固定和自定义）都使用这一个产品，通过 customPrice 锁定金额。
                    产品需要设置为允许动态定价（quantityBehavior: LOCK）。
                  </span>
                }
              >
                <Input placeholder="blueapi-ai-product" />
              </Form.Item>

              <Form.Item label="订单超时时间">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <InputNumber
                    min={60}
                    max={3600}
                    value={fastspringTimeout}
                    onChange={(value) => setFastSpringTimeout(value || 300)}
                    style={{ width: 120 }}
                  />
                  <span style={{ color: '#6b7280' }}>秒</span>
                  <span style={{ marginLeft: 8, color: '#999', fontSize: 12 }}>
                    （60-3600秒，建议5分钟=300秒）
                  </span>
                </div>
              </Form.Item>

              <Form.Item label="运行环境">
                <Switch
                  checkedChildren="生产"
                  unCheckedChildren="测试"
                  checked={fastspringEnv === 'production'}
                  onChange={(checked) => setFastSpringEnv(checked ? 'production' : 'test')}
                />
                <span style={{ marginLeft: 8, color: '#6b7280' }}>
                  {fastspringEnv === 'production' ? '生产环境' : '测试环境'}
                </span>
              </Form.Item>
            </Card>

            {/* Airwallex 配置 */}
            <Card
              title={
                <span>
                  <GlobalOutlined style={{ marginRight: 8 }} />
                  Airwallex 支付
                </span>
              }
              style={{ marginBottom: 24 }}
              extra={
                <Switch
                  checkedChildren="启用"
                  unCheckedChildren="禁用"
                  checked={airwallexEnabled}
                  onChange={setAirwallexEnabled}
                />
              }
            >
              <Alert
                message="获取 Airwallex API 凭证"
                description={
                  <span>
                    请前往 <a href="https://www.airwallex.com/app/settings/developer" target="_blank" rel="noopener noreferrer">Airwallex Developer Settings</a> 获取 Client ID 和 API Key。
                    Webhook 端点地址为：<code>/api/webhook/airwallex</code>
                  </span>
                }
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
              <Form.Item
                name="airwallex_client_id"
                label="Airwallex Client ID"
                rules={[{ required: airwallexEnabled, message: '请输入 Client ID' }]}
              >
                <Input placeholder="Client ID" />
              </Form.Item>
              <Form.Item
                name="airwallex_api_key"
                label="Airwallex API Key"
                rules={[{ required: airwallexEnabled, message: '请输入 API Key' }]}
              >
                <Input.Password placeholder="API Key" />
              </Form.Item>
              <Form.Item
                name="airwallex_webhook_secret"
                label="Airwallex Webhook Secret"
                extra="用于验证 Webhook HMAC SHA256 签名，在 Airwallex Webhook 设置页面获取。"
              >
                <Input.Password placeholder="whsec_xxx" />
              </Form.Item>
              <Form.Item
                name="airwallex_api_base_url"
                label="Airwallex API Base URL"
                extra="Demo: https://api-demo.airwallex.com | 生产: https://api.airwallex.com"
              >
                <Input placeholder="https://api-demo.airwallex.com" />
              </Form.Item>
              <Form.Item label="运行环境">
                <Switch
                  checkedChildren="生产"
                  unCheckedChildren="Demo"
                  checked={airwallexEnv === 'prod'}
                  onChange={(checked) => setAirwallexEnv(checked ? 'prod' : 'demo')}
                />
                <span style={{ marginLeft: 8, color: '#6b7280' }}>
                  {airwallexEnv === 'prod' ? '生产环境' : 'Demo 测试环境'}
                </span>
              </Form.Item>
            </Card>

            {/* 保存按钮 */}
            <Button
              type="primary"
              size="large"
              icon={<SaveOutlined />}
              onClick={handleSave}
              loading={saving}
            >
              保存配置
            </Button>
          </Form>
        </Spin>
      </div>
    </div>
  );
};

export default PaymentSettings;
